import type { useTranslations } from "next-intl";

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const ABSOLUTE_FORMAT: Intl.DateTimeFormatOptions = {
  hour: "2-digit",
  minute: "2-digit",
  month: "short",
  day: "numeric",
};

type TimeUnit = "second" | "minute" | "hour";
type UnitDisplay = NonNullable<Intl.NumberFormatOptions["unitDisplay"]>;

const formatterCache = new Map<string, Intl.NumberFormat>();

function formatUnit(value: number, unit: TimeUnit, display: UnitDisplay, locale: string): string {
  const key = `${locale}|${unit}|${display}`;
  let formatter = formatterCache.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, { style: "unit", unit, unitDisplay: display });
    formatterCache.set(key, formatter);
  }
  return formatter.format(value);
}

function isSameCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

// Compact form for chat: "10s", "5m", "3h", then an absolute date-time
export function formatRelativeTime(
  isoTimestamp: string,
  locale: string,
  minUnit: "seconds" | "minute",
  t: ReturnType<typeof useTranslations>,
): string {
  const then = new Date(isoTimestamp);
  const diff = Date.now() - then.getTime();

  if (minUnit === "seconds") {
    const diffSeconds = Math.floor(diff / SECOND);
    if (diffSeconds < 10) return t("justNow");
    if (diffSeconds < 60) {
      return formatUnit(Math.floor(diffSeconds / 10) * 10, "second", "narrow", locale);
    }
  } else if (diff < MINUTE) {
    return t("justNow");
  }

  if (diff < HOUR) return formatUnit(Math.floor(diff / MINUTE), "minute", "narrow", locale);
  if (diff < DAY) return formatUnit(Math.floor(diff / HOUR), "hour", "narrow", locale);

  return then.toLocaleString(locale, ABSOLUTE_FORMAT);
}

// Spelled-out form for notifications: "2 min ago", "3 hours ago", "Yesterday", "Nov 15"
export function formatNotificationTime(
  isoTimestamp: string,
  locale: string,
  t: ReturnType<typeof useTranslations>,
): string {
  const then = new Date(isoTimestamp);
  const now = new Date();
  const diff = now.getTime() - then.getTime();

  if (diff < MINUTE) return t("justNow");
  if (diff < HOUR) {
    return t("ago", { span: formatUnit(Math.floor(diff / MINUTE), "minute", "short", locale) });
  }
  if (isSameCalendarDay(then, now)) {
    return t("ago", { span: formatUnit(Math.floor(diff / HOUR), "hour", "long", locale) });
  }

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (isSameCalendarDay(then, yesterday)) return t("yesterday");

  return then.toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
    ...(then.getFullYear() !== now.getFullYear() && { year: "numeric" }),
  });
}
